import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Shield, FileText, Briefcase, Users, Lock, CheckCircle, FolderKanban } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Logo } from '../components/Logo';
import { MODULES } from '../hooks/useEntitlements';
import { useForcedDarkMode } from '../contexts/ThemeContext';

// Grid Background Component
const GridBackground = () => (
  <div className="absolute inset-0 overflow-hidden">
    <svg className="absolute inset-0 w-full h-full opacity-20">
      <defs>
        <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#2D7D6F" strokeWidth="0.5"/>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
    <div 
      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full opacity-30 blur-3xl pointer-events-none"
      style={{ background: 'radial-gradient(ellipse, #2D7D6F 0%, transparent 60%)' }}
    />
  </div>
);

// Module-specific content
const MODULE_CONTENT = {
  grc: {
    metaTitle: "GRC Software | Simplif-i",
    metaDescription: "Compliance that works in real operations. Not just on paper. 30+ frameworks, evidence that maps itself, audits without the panic.",
    h1: "Compliance that works.",
    h1Highlight: "In real life.",
    heroSubtitle: "30+ frameworks. Evidence that maps itself. Audits without the scramble.",
    problemTitle: "You know the drill.",
    problemPoints: [
      "Auditor emails. Panic stations.",
      "Evidence scattered across 47 folders.",
      "Controls that exist on paper. Nowhere else."
    ],
    solutionTitle: "We built it differently.",
    solutionPoints: [
      "Controls mapped to what you actually do",
      "Evidence where it should be. Always.",
      "Risk tracked as it exists. Not as you hope it does.",
      "Audit-ready. Not audit-scrambling."
    ],
    features: [
      "30+ compliance frameworks",
      "Risk register",
      "Data protection module",
      "AI governance assistant",
      "Audit management",
      "QMS included"
    ],
    closeTitle: "Compliance should help you move.",
    closeSubtitle: "Not hold you back.",
    icon: Shield,
    // Pricing - Single tier for GRC (Standard)
    pricing: {
      regular: 499,
      founding: 149,
      seats: 5,
      extraSeat: 49,
      aiCredits: 150
    }
  },
  contracts: {
    metaTitle: "Contract Management | Simplif-i",
    metaDescription: "Contracts that stay alive after signing. Obligations tracked. Renewals flagged. Risk visible. Connected to real delivery.",
    h1: "Contracts that stay alive.",
    h1Highlight: "After signing.",
    heroSubtitle: "Obligations tracked. Renewals flagged. Connected to what's actually being delivered.",
    problemTitle: "Signed. Filed. Forgotten.",
    problemPoints: [
      "Renewal hits. Nobody saw it coming.",
      "Obligation missed. Finger pointing starts.",
      "Risk buried in page 47. Nobody read page 47."
    ],
    solutionTitle: "Contracts that work for you.",
    solutionPoints: [
      "Full lifecycle. Creation to renewal.",
      "Obligations tied to real delivery",
      "Risk surfaced before it bites",
      "Everything findable. Everything structured."
    ],
    features: [
      "Full contract lifecycle",
      "Obligation tracking",
      "Renewal management",
      "Risk scoring",
      "Vendor management",
      "AI contract analysis"
    ],
    closeTitle: "Contracts run your business.",
    closeSubtitle: "Treat them like it.",
    icon: FileText,
    // Contracts is part of Pro
    pricing: {
      regular: 499,
      founding: 149,
      seats: 5,
      extraSeat: 49,
      aiCredits: 150,
      tier: 'Pro'
    }
  },
  pmo: {
    metaTitle: "PMO Software | Simplif-i",
    metaDescription: "Project delivery that shows reality. Not polished status reports. Track what's actually happening across projects, resources, outcomes.",
    h1: "Delivery without",
    h1Highlight: "the theatre.",
    heroSubtitle: "See what's actually happening. Not what people say is happening.",
    problemTitle: "Status: Green. Reality: On fire.",
    problemPoints: [
      "Reports polished. Problems hidden.",
      "Risks surfaced after they've landed.",
      "Three tools. Three versions of the truth."
    ],
    solutionTitle: "PMO that shows reality.",
    solutionPoints: [
      "Real progress. Not status theatre.",
      "Risks visible before they're problems",
      "Resources managed. Not guessed at.",
      "Issues early. Not post-mortems."
    ],
    features: [
      "Project portfolio management",
      "Resource planning",
      "RASCI matrix",
      "Sentiment tracking",
      "Dependency mapping",
      "Custom dashboards"
    ],
    closeTitle: "You can't fix what you can't see.",
    closeSubtitle: "Start seeing.",
    icon: FolderKanban,
    // PMO is part of Pro
    pricing: {
      regular: 499,
      founding: 149,
      seats: 5,
      extraSeat: 49,
      aiCredits: 150,
      tier: 'Pro'
    }
  },
  board: {
    metaTitle: "Board Management Software | Simplif-i",
    metaDescription: "Board visibility that reflects reality. Live data, not reconstructed narratives. Secure, structured, connected to operations.",
    h1: "Boards that see",
    h1Highlight: "what's real.",
    heroSubtitle: "Live visibility into operations. Not last month's PowerPoint.",
    problemTitle: "Boards see what you show them.",
    problemPoints: [
      "Data reconstructed. Polished. Delayed.",
      "Decisions on partial information.",
      "Governance reactive. Not proactive."
    ],
    solutionTitle: "Show them what's actually happening.",
    solutionPoints: [
      "Live board packs. Real data.",
      "Meetings structured. Decisions tracked.",
      "Governance connected to operations",
      "Secure. Compliant. Auditable."
    ],
    features: [
      "Live board packs",
      "Meeting management",
      "Decision tracking",
      "Secure messaging",
      "Governance visibility",
      "Compliance reporting"
    ],
    closeTitle: "Better information.",
    closeSubtitle: "Better decisions.",
    icon: Users,
    // Board is part of Pro
    pricing: {
      regular: 499,
      founding: 149,
      seats: 5,
      extraSeat: 49,
      aiCredits: 150,
      tier: 'Pro'
    }
  },
  ma: {
    metaTitle: "M&A Software | Simplif-i",
    metaDescription: "Due diligence that shows reality. See contracts, risks, operations before you buy. Not after.",
    h1: "Know what you're buying.",
    h1Highlight: "Before you buy it.",
    heroSubtitle: "Due diligence with structure. Data rooms that work. Surprises eliminated.",
    problemTitle: "Spreadsheets. Static docs. Surprises.",
    problemPoints: [
      "Data fragmented across 200 files.",
      "Risks discovered post-close.",
      "Integration planned on guesswork."
    ],
    solutionTitle: "See the business. Properly.",
    solutionPoints: [
      "Structured due diligence workflows",
      "Virtual data rooms that actually work",
      "Contract and risk visibility upfront",
      "Integration planned on real data"
    ],
    // M&A has two tiers
    hasTwoTiers: true,
    tiers: {
      pro: {
        name: 'M&A Pro',
        regular: 499,
        founding: 149,
        features: [
          "Deal pipeline management",
          "Target tracking",
          "Basic due diligence",
          "Document management",
          "Team collaboration",
          "150 AI credits/month"
        ]
      },
      proPlus: {
        name: 'M&A Pro+',
        regular: null, // POA
        founding: null, // POA
        features: [
          "Everything in Pro",
          "Virtual data rooms",
          "Advanced due diligence workflows",
          "Integration playbooks",
          "External party access",
          "Custom AI credit allocation"
        ]
      }
    },
    closeTitle: "Bad visibility. Bad deals.",
    closeSubtitle: "Get visibility.",
    icon: Briefcase
  }
};

const ModuleSalesPage = ({ moduleId }) => {
  useForcedDarkMode();
  const navigate = useNavigate();

  const content = MODULE_CONTENT[moduleId] || MODULE_CONTENT.grc;
  const moduleInfo = MODULES[moduleId] || {};
  const Icon = content.icon || Shield;

  useEffect(() => {
    document.title = content.metaTitle;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = content.metaDescription;
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    const prev = robots.content;
    robots.content = 'noindex, follow';
    return () => { robots.content = prev; };
  }, [content]);

  const handleGetStarted = (tier) => {
    navigate(`/signup?module=${moduleId}&tier=${tier}`);
  };

  // Render M&A page with two tiers
  if (content.hasTwoTiers) {
    return (
      <div className="min-h-screen bg-[#0a0f0d] text-white">
        {/* Navigation */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0f0d]/90 backdrop-blur-sm border-b border-[#2D7D6F]/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <Logo className="h-8 w-auto" showText={false} />
                <span className="text-xl font-semibold text-white">Simplif-i</span>
              </Link>
              <div className="flex items-center gap-4">
                <Link to="/login" className="text-white hover:text-[#2D7D6F] transition-colors text-sm font-medium">Login</Link>
                <Link to="/signup">
                  <Button className="bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold px-5">Start free trial</Button>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative pt-32 pb-20 overflow-hidden">
          <GridBackground />
          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
              {content.h1}
              <br />
              <span className="text-[#2D7D6F]">{content.h1Highlight}</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/70 mb-10 max-w-xl mx-auto">{content.heroSubtitle}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <Button size="lg" className="w-full sm:w-auto bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-14 px-8 text-base rounded-lg" onClick={() => handleGetStarted('pro')}>
                Start free trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/5 h-14 px-8 text-base rounded-lg" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>
                See pricing
              </Button>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 text-white/40 text-sm">
              <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#2D7D6F]" /><span>7-day free trial</span></div>
              <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-[#2D7D6F]" /><span>Card at checkout · charged day 8</span></div>
              <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#2D7D6F]" /><span>Cancel anytime</span></div>
            </div>
          </div>
        </section>

        {/* Problem Section */}
        <section className="py-20 bg-[#0d1210] border-y border-[#2D7D6F]/10">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-10" style={{ color: '#2D7D6F' }}>{content.problemTitle}</h2>
            <div className="space-y-4">
              {content.problemPoints.map((point, idx) => (
                <p key={idx} className="text-lg text-white/60">{point}</p>
              ))}
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-20 relative overflow-hidden">
          <GridBackground />
          <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-10 text-center text-[#2D7D6F]">{content.solutionTitle}</h2>
            <div className="space-y-3">
              {content.solutionPoints.map((point, idx) => (
                <div key={idx} className="flex items-start gap-4 p-5 bg-[#2D7D6F]/5 rounded-lg border border-[#2D7D6F]/10">
                  <CheckCircle className="h-5 w-5 text-[#2D7D6F] mt-0.5 shrink-0" />
                  <p className="text-white/80">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section - Two Tiers */}
        <section id="pricing" className="py-20 bg-[#0d1210] border-y border-[#2D7D6F]/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#2D7D6F' }}>Two options. Pick what fits.</h2>
            <p className="text-white/50 text-center mb-10">7-day free trial on Pro. Pro+ scoped to your needs.</p>

            <div className="grid md:grid-cols-2 gap-6">
              {/* M&A Pro */}
              <Card className="bg-[#0a0f0d] border-2 border-[#2D7D6F] relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold bg-[#2D7D6F] text-[#0a0f0d]">
                  Founding Member
                </div>
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-1" style={{ color: '#2D7D6F' }}>{content.tiers.pro.name}</h3>
                  <p className="text-white/50 text-sm mb-4">Core M&A capabilities</p>
                  
                  <div className="mb-2">
                    <span className="text-lg line-through mr-2" style={{ color: 'rgba(255,255,255,0.4)' }}>£{content.tiers.pro.regular}</span>
                    <span className="text-4xl font-bold" style={{ color: '#2D7D6F' }}>£{content.tiers.pro.founding}</span>
                    <span className="text-white/50 text-sm">/month</span>
                  </div>
                  <p className="text-[#2D7D6F] text-sm mb-6">Founding price · full platform included</p>

                  <Button className="w-full mb-6 bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-12" onClick={() => handleGetStarted('pro')}>
                    Start free trial <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>

                  <ul className="space-y-3">
                    {content.tiers.pro.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-white/70">
                        <Check className="h-4 w-4 text-[#2D7D6F] mt-0.5 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>

              {/* M&A Pro+ */}
              <Card className="bg-[#0a0f0d] border border-[#2D7D6F]/30">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-1" style={{ color: '#F59E0B' }}>{content.tiers.proPlus.name}</h3>
                  <p className="text-white/50 text-sm mb-4">Advanced M&A tooling</p>
                  
                  <div className="mb-2">
                    <span className="text-3xl font-bold" style={{ color: '#F59E0B' }}>POA</span>
                  </div>
                  <p className="text-white/50 text-sm mb-6">Price on application · Scoped to your needs</p>

                  <Button className="w-full mb-6 bg-transparent border border-[#2D7D6F] text-[#2D7D6F] hover:bg-[#2D7D6F]/10 h-12" onClick={() => navigate('/contact?interest=ma-pro-plus')}>
                    Contact us <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>

                  <ul className="space-y-3">
                    {content.tiers.proPlus.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-sm text-white/70">
                        <Check className="h-4 w-4 text-[#2D7D6F] mt-0.5 shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>

            <p className="text-center text-white/40 text-sm mt-8">
              Founding price £149/month or £1,490/year (12 months for the price of 10) · usual price £499/month · 7-day free trial
            </p>
          </div>
        </section>

        {/* Close Section */}
        <section className="py-24 relative overflow-hidden">
          <GridBackground />
          <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#2D7D6F' }}>{content.closeTitle}</h2>
            <p className="text-[#2D7D6F] text-xl mb-10">{content.closeSubtitle}</p>
            <Button size="lg" className="bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-14 px-8 text-base rounded-lg" onClick={() => handleGetStarted('pro')}>
              Start free trial <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 border-t border-[#2D7D6F]/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <Link to="/" className="flex items-center gap-2">
                <Logo className="h-6 w-auto" showText={false} />
                <span className="font-semibold text-white">Simplif-i</span>
              </Link>
              <div className="flex items-center gap-6 text-sm text-white/40">
                <Link to="/pricing" className="hover:text-white">Pricing</Link>
                <Link to="/privacy" className="hover:text-white">Privacy</Link>
                <Link to="/terms" className="hover:text-white">Terms</Link>
              </div>
              <p className="text-xs text-white/30">© 2026 Simplif-i</p>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // Standard single-tier page for all other modules
  return (
    <div className="min-h-screen bg-[#0a0f0d] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0f0d]/90 backdrop-blur-sm border-b border-[#2D7D6F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <Logo className="h-8 w-auto" showText={false} />
              <span className="text-xl font-semibold text-white">Simplif-i</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login" className="text-white hover:text-[#2D7D6F] transition-colors text-sm font-medium">Login</Link>
              <Link to="/signup">
                <Button className="bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold px-5">Start free trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <GridBackground />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight tracking-tight">
            {content.h1}
            <br />
            <span className="text-[#2D7D6F]">{content.h1Highlight}</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/70 mb-10 max-w-xl mx-auto">{content.heroSubtitle}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button size="lg" className="w-full sm:w-auto bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-14 px-8 text-base rounded-lg" onClick={() => handleGetStarted('founding')}>
              Start free trial <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto border-white/30 text-white hover:bg-white/5 h-14 px-8 text-base rounded-lg" onClick={() => document.getElementById('pricing').scrollIntoView({ behavior: 'smooth' })}>
              See pricing
            </Button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-white/40 text-sm">
            <div className="flex items-center gap-2"><Lock className="h-4 w-4 text-[#2D7D6F]" /><span>7-day free trial</span></div>
            <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-[#2D7D6F]" /><span>Card at checkout · charged day 8</span></div>
            <div className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-[#2D7D6F]" /><span>Cancel anytime</span></div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-[#0d1210] border-y border-[#2D7D6F]/10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-10" style={{ color: '#2D7D6F' }}>{content.problemTitle}</h2>
          <div className="space-y-4">
            {content.problemPoints.map((point, idx) => (
              <p key={idx} className="text-lg text-white/60">{point}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 relative overflow-hidden">
        <GridBackground />
        <div className="relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold mb-10 text-center text-[#2D7D6F]">{content.solutionTitle}</h2>
          <div className="space-y-3">
            {content.solutionPoints.map((point, idx) => (
              <div key={idx} className="flex items-start gap-4 p-5 bg-[#2D7D6F]/5 rounded-lg border border-[#2D7D6F]/10">
                <CheckCircle className="h-5 w-5 text-[#2D7D6F] mt-0.5 shrink-0" />
                <p className="text-white/80">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-[#0d1210] border-y border-[#2D7D6F]/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-center mb-10 text-white/60">What's included</h2>
          <div className="flex flex-wrap justify-center gap-4">
            {content.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 px-4 py-2 bg-[#0a0f0d] border border-[#2D7D6F]/20 rounded-full">
                <Check className="h-4 w-4 text-[#2D7D6F]" />
                <span className="text-sm text-white/70">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Single Tier */}
      <section id="pricing" className="py-20 relative overflow-hidden">
        <GridBackground />
        <div className="relative z-10 max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-[#0d1210] border-2 border-[#2D7D6F] relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold bg-[#2D7D6F] text-[#0a0f0d]">
              Founding Member
            </div>
            <CardContent className="p-8 text-center">
              <h3 className="text-xl font-bold text-[#2D7D6F] mb-2">
                Simplif-i {content.pricing?.tier || 'Standard'}
              </h3>
              <p className="text-white/50 text-sm mb-6">Everything you need. One price.</p>
              
              <div className="mb-2 flex items-baseline justify-center gap-3">
                <span className="text-white/50 text-xl line-through">£{content.pricing?.regular || 299}</span>
                <span className="text-5xl font-bold text-[#2D7D6F]">£{content.pricing?.founding || 149}</span>
                <span className="text-white/60 text-lg">/month</span>
              </div>
              <p className="text-white/60 text-sm mb-6">
                Founding price, locked while your subscription stays active · full platform included
              </p>

              <Button className="w-full mb-6 bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-12" onClick={() => handleGetStarted('founding')}>
                Start free trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <ul className="space-y-3 text-left">
                {content.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-sm text-white/70">
                    <Check className="h-4 w-4 text-[#2D7D6F] mt-0.5 shrink-0" />
                    {feature}
                  </li>
                ))}
                <li className="flex items-start gap-3 text-sm text-white/70">
                  <Check className="h-4 w-4 text-[#2D7D6F] mt-0.5 shrink-0" />
                  {content.pricing?.aiCredits || 150} AI credits/month
                </li>
              </ul>
              
              <div className="mt-6 pt-6 border-t border-[#2D7D6F]/10">
                <p className="text-white/40 text-xs mb-2">Or £1,490/year founding (12 months for the price of 10)</p>
                <div className="flex items-center justify-center gap-6 text-white/40 text-xs">
                  <span className="flex items-center gap-1"><Lock className="h-3 w-3" /> 7-day free trial</span>
                  <span className="flex items-center gap-1"><Shield className="h-3 w-3" /> Cancel anytime</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <p className="text-center text-white/40 text-sm mt-6">
            Already on Simplif-i? <Link to="/account" className="text-[#2D7D6F] hover:underline">Add this module</Link>
          </p>
        </div>
      </section>

      {/* Close Section */}
      <section className="py-24 bg-[#0d1210] border-t border-[#2D7D6F]/10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#2D7D6F' }}>{content.closeTitle}</h2>
          <p className="text-[#2D7D6F] text-xl mb-10">{content.closeSubtitle}</p>
          <Button size="lg" className="bg-[#2D7D6F] hover:bg-[#2D7D6F]/90 text-[#0a0f0d] font-semibold h-14 px-8 text-base rounded-lg" onClick={() => handleGetStarted('founding')}>
            Start free trial <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-[#2D7D6F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Logo className="h-6 w-auto" showText={false} />
              <span className="font-semibold text-white">Simplif-i</span>
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <Link to="/privacy" className="hover:text-white">Privacy</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
            </div>
            <p className="text-xs text-white/30">© 2026 Simplif-i</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export const GRCSalesPage = () => <ModuleSalesPage moduleId="grc" />;
export const ContractsSalesPage = () => <ModuleSalesPage moduleId="contracts" />;
export const PMOSalesPage = () => <ModuleSalesPage moduleId="pmo" />;
export const MASalesPage = () => <ModuleSalesPage moduleId="ma" />;
export const BoardSalesPage = () => <ModuleSalesPage moduleId="board" />;

export default ModuleSalesPage;
